import { useState } from "react";
import { ServiceCommissionOverride } from "../../../types/commission";
import {
  DraftsToOverridesResult,
  ServiceOverrideDrafts,
  describeDraftRates,
  draftsToOverrides,
  overridesToDrafts,
} from "../../../utils/serviceCommissionOverrides";

export interface ServiceOverrideDraftsState {
  drafts: ServiceOverrideDrafts;
  setDrafts: (next: ServiceOverrideDrafts) => void;
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  /** Those services' rates, A–Z, e.g. "Balayage 50%" (summary preview). */
  previewRates: string[];
  /** Service names by id, so validation errors can name the service. Set by
   * ServiceSpecificRatesScreen, which has the salon's service list. */
  setServiceNames: (names: Record<string, string>) => void;
  /** The wire list to submit: `[]` when switched off (clears saved rates). */
  toPayload: () => DraftsToOverridesResult;
}

/**
 * Editing state for the commission form's service-specific rates. Seeded from
 * the record being edited (or the staff member's current rates when adding a
 * new effective-dated rate); service-specific mode starts on when any rate
 * exists. In that mode the record's default service rate is saved as 0%, so a
 * service without a rate earns nothing.
 */
export function useServiceOverrideDrafts(
  initial: ServiceCommissionOverride[] | undefined | null,
): ServiceOverrideDraftsState {
  const [drafts, setDrafts] = useState<ServiceOverrideDrafts>(() =>
    overridesToDrafts(initial),
  );
  const [enabled, setEnabled] = useState<boolean>(
    () => (initial?.length ?? 0) > 0,
  );
  const [serviceNames, setServiceNames] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (initial || []).map((o) => [
        o.salon_service_id,
        o.salon_service_name || "",
      ]),
    ),
  );
  const toPayload = (): DraftsToOverridesResult =>
    enabled
      ? draftsToOverrides(drafts, serviceNames)
      : { overrides: [], error: null };
  return {
    drafts,
    setDrafts,
    enabled,
    setEnabled,
    previewRates: describeDraftRates(drafts, serviceNames),
    setServiceNames,
    toPayload,
  };
}
